import classNames from "classnames";
import { debounce, filter, find, findIndex, isNumber, isString, map, reject, uniqBy } from "lodash";
import { Button } from "PFComponents/button";
import { NO_ITEM } from "PFComponents/dropdown/dropdown_item";
import Select from "PFComponents/select/select";
import InputFieldSet from "PFComponents/text/input_field_set";
import FilterCleanIcon from "PFIcons/filter-clean.svg";
import HistoryIcon from "PFIcons/history.svg";
import PropTypes from "prop-types";
import React from "react";

import canonicalId from "../../helpers/canonicalId";
import { LoadingDots } from "../loading_dots/loading_dots";
import css from "./autoselect.module.scss";
import AutoSelectValues from "./autoselect_values";

const canonicalValue = (item) => ({
  id: canonicalId(item.text || item.value),
  displayElement: item.text || item.value,
  item: item
});

class AutoSelect extends React.Component {
  // eslint-disable-next-line react/sort-comp
  queryOptions = () => {
    const { query, cache } = this.props;
    const { inputValue } = this.state;

    if (this.isUnmounted) {
      return;
    }

    const term = canonicalId(inputValue || "");

    if (this.xhr) {
      this.xhr.abort && this.xhr.abort();
      this.xhr = null;
    }

    if (this.cachedOptions[term]) {
      this.setState({ dropDownOptions: this.cachedOptions[term] });
    } else {
      this.setState(
        {
          dropDownOptions: [{ id: "loading", displayElement: <LoadingDots />, item: NO_ITEM }]
        },
        () => {
          this.xhr = query(term);
          this.xhr.then((resp) => {
            if (this.isUnmounted) {
              return;
            }
            const options = this.parseResponse(resp);
            if (cache === true) {
              this.cachedOptions[term] = options;
            }
            this.setState({ dropDownOptions: options });
          });
        }
      );
    }
  };

  constructor(props) {
    super(props);

    this.props.saveRef && this.props.saveRef(this);
    this.selectRef = React.createRef();
    this.autoSelectRef = React.createRef();
    this.handleClick = this.handleClick.bind(this);

    this.state = {
      inputValue: "",
      dropDownOptions: [],
      values: (this.props.values || []).map((value) => canonicalValue(value))
    };

    this.cachedOptions = {};
    this.debouncedQueryOptions = debounce(this.queryOptions, this.props.debounceWait);
  }

  componentDidMount() {
    document.addEventListener("click", this.handleClick);
  }

  componentDidUpdate(prevProps) {
    if (this.props.values !== prevProps.values) {
      this.setState({ values: (this.props.values || []).map((value) => canonicalValue(value)) });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    document.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    const modalSelector = document.getElementById("modal_region");

    if (!modalSelector || (modalSelector && !modalSelector.contains(event.target))) {
      if (
        this.autoSelectRef &&
        this.autoSelectRef.current &&
        !this.autoSelectRef.current.contains(event.target)
      ) {
        this.setState({ isFocused: false });
      } else {
        this.setState({ isFocused: true });
      }
    }
  }

  setValues = (values) => {
    this.setState({ values: (values || []).map((value) => canonicalValue(value)) });
  };

  filterOptions = () => {
    const { multi, allowSameValueInOptions } = this.props;
    const { dropDownOptions, values } = this.state;

    if (!multi && allowSameValueInOptions) {
      return uniqBy(dropDownOptions, "id");
    } else {
      return uniqBy(dropDownOptions, "id").filter(
        (option) =>
          !find(values, { id: option.id }) &&
          !find(
            values.map(({ item }) => item),
            { id: option.id }
          )
      );
    }
  };

  parseResponse = (response) => {
    const { parseResponse, formatOption, filterOptions } = this.props;

    const { inputValue } = this.state;

    if (parseResponse) {
      response = parseResponse(response);
    }

    if (filterOptions) {
      response = filterOptions(response, canonicalId(inputValue));
    }

    if (formatOption) {
      return response.map((option) => formatOption(option)).filter(Boolean);
    }

    // Here to avoid objects passed from API that are not supported (location) without proper parsing
    response = filter(response, (item) => isString(item.text) || isNumber(item.text));

    return response.map((option) => ({
      id: canonicalId(option.text),
      displayElement: option.displayElement || <span>{option.text}</span>,
      item: option,
      disabled: option.disabled
    }));
  };

  getDropDownOptions = () => {
    const { inputValue, values } = this.state;
    const { letCreate, maxLength } = this.props;

    if (maxLength && values.length >= maxLength) {
      return [];
    }

    const filteredDropDownOptions = this.filterOptions();

    const term = canonicalId(inputValue);

    if (letCreate && term && term.length > 0) {
      const termInOptions = find(filteredDropDownOptions, (option) => option.id === term);

      if (!termInOptions) {
        const termInValues = find(values, (value) => value.id === term);

        if (!termInValues) {
          const isTermDisabledValue =
            this.props.disabledValues.length > 0 && this.props.disabledValues.includes(term);

          if (!isTermDisabledValue) {
            filteredDropDownOptions.unshift({
              id: term,
              displayElement: (
                <span className={css.new}>
                  Create &ldquo;<span>{inputValue}</span>&rdquo;
                </span>
              ),
              item: { id: canonicalId(inputValue), text: inputValue, created: true }
            });
          }
        }
      }
    }
    return filteredDropDownOptions;
  };

  // Dropdown Handlers
  handleDropDownOpen = () => {
    this.setState({ isFocused: true });
    this.debouncedQueryOptions();
  };

  handleDropDownChange = (item) => {
    const { multi, handleChange } = this.props;
    const values = multi ? this.state.values : [];
    const items = Array.isArray(item) ? item : [item];
    items.map((item) => values.push(canonicalValue(item)));

    this.setState({ values, inputValue: "" });
    handleChange && handleChange(map(values, "item"));
  };

  handleBadgeDateChange = (item, event) => {
    event.stopPropagation();
    event.preventDefault();

    const { handleChange } = this.props;
    const values = this.state.values.filter((changedItem) => changedItem.item.id !== item.id);

    values.push(canonicalValue(item));

    this.setState({ values });

    handleChange && handleChange(map(values, "item"));
  };

  handleDropDownClose = () => {
    const { multi } = this.props;

    this.setState({ inputValue: "", dropDownOptions: [] });
    if (!multi) {
      this.setState({ isFocused: false });
    }
    this.props.handleDropDownOpen?.(false);
  };

  // Input Handlers
  handleInputChange = (value) => {
    this.setState({ inputValue: value }, this.debouncedQueryOptions);
  };

  handleRemove = (value, e) => {
    e.preventDefault();
    e.stopPropagation();

    const { handleChange, handleRemove, disabled } = this.props;
    const { values } = this.state;

    if (disabled) {
      return;
    }

    const newVals = reject(values, { id: value.id });

    this.setState({ values: newVals });
    handleChange && handleChange(map(newVals, "item"));
    handleRemove && handleRemove(map(newVals, "item"), value.item);
  };

  handleClearLinkClick = (event) => {
    const { handleChange } = this.props;

    event && event.stopPropagation();
    event && event.preventDefault();
    this.setState({ isFocused: false, inputValue: "", dropDownOptions: [], values: [] });

    handleChange && handleChange([]);
  };

  render() {
    const {
      kind,
      label,
      locked,
      lockedTip,
      lockedTipPosition,
      letClear,
      tip,
      whiteStyle,
      multi,
      error,
      disabled,
      style,
      selectStyles,
      inputStyle,
      noPadding,
      closeOnChange,
      maxLength,
      qaId,
      id,
      alwaysEmpty,
      allowSameValueInOptions,
      rootClassName,
      placeholder,
      displayValues,
      required,
      icon,
      title,
      onRestore,
      portalRef
    } = this.props;

    const { inputValue, isFocused } = this.state;

    const { values } = this.state;

    const active = !!inputValue || values.length > 0 || isFocused || placeholder?.length > 0;
    const showClearLink = !locked && letClear && !disabled && (values.length > 0 || displayValues);
    const options = this.getDropDownOptions();
    const selectedIndex =
      allowSameValueInOptions && !multi && values.length > 0
        ? findIndex(options, ({ id }) => id === values[0].id)
        : null;

    if (alwaysEmpty && values.length > 0) {
      this.setState({ values: [] });
    }

    return (
      <div
        ref={this.autoSelectRef}
        data-qa-id={qaId}
        id={id}
        style={style}
        className={classNames(
          css.autoselect,
          { [css.single]: !multi },
          { [css.multi]: multi },
          { [css.locked]: locked },
          {
            [css.inputDown]:
              !disabled && ((isFocused && values.length > 0) || values.length === 0 || placeholder)
          },
          rootClassName
        )}
      >
        <div className={css.actions}>
          {onRestore && this.renderRestoreLink()}
          {showClearLink && this.renderClearLink()}
        </div>
        <Select
          portalRef={portalRef}
          kind={kind}
          ref={this.selectRef}
          placeholder={placeholder}
          label={label}
          title={title}
          locked={locked}
          lockedTip={lockedTip}
          lockedTipPosition={lockedTipPosition}
          error={error}
          tip={tip}
          disabled={disabled}
          value={inputValue}
          isFull={!!(maxLength && values.length >= maxLength)}
          controlledValue
          selectedIndex={selectedIndex}
          icon={icon}
          displayValue={!multi && (displayValues || this.renderValue())}
          displayValues={multi && (displayValues || this.renderValues())}
          closeOnChange={closeOnChange}
          whiteStyle={whiteStyle}
          active={active}
          style={selectStyles}
          inputStyle={inputStyle}
          options={options}
          handleOpen={this.handleDropDownOpen}
          handleInputChange={this.handleInputChange}
          handleChange={this.handleDropDownChange}
          handleClose={this.handleDropDownClose}
          preOptions={this.props.preOptions}
          handleDropDownToggle={this.props.handleDropDownToggle}
          noPadding={noPadding}
          isDropDownAccesible={false}
          required={required}
          multi={multi}
        />
      </div>
    );
  }

  renderClearLink = () => (
    <Button kind="text" rootClassName={css.actionLink} onClick={this.handleClearLinkClick}>
      <FilterCleanIcon height={16} width={16} />
    </Button>
  );

  renderRestoreLink = () => (
    <Button kind="text" rootClassName={css.actionLink} onClick={this.props.onRestore}>
      <HistoryIcon height={16} width={16} />
    </Button>
  );

  renderValue() {
    const { values } = this.state;
    const value = values[0];
    return value && value.displayElement;
  }

  renderValues = () => {
    const { disabled, hasValuesWithExpiryDate, locked, multi, renderItem, suffixElement } = this.props;
    if (!this.props.showValues || !this.state.values || this.state.values.length === 0) {
      return null;
    }

    return (
      <AutoSelectValues
        values={this.state.values}
        onRemove={(val, e) => this.handleRemove(val, e)}
        onDateChange={(item, e) => this.handleBadgeDateChange(item, e)}
        locked={locked}
        multi={multi}
        disabled={disabled}
        suffixElement={suffixElement}
        hasValuesWithExpiryDate={hasValuesWithExpiryDate}
        renderItem={renderItem}
      />
    );
  };
}

AutoSelect.propTypes = {
  kind: InputFieldSet.propTypes.kind,
  multi: PropTypes.bool,
  allowSameValueInOptions: PropTypes.bool, // only works with multi==false
  values: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.any,
      text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      locked: PropTypes.bool
    })
  ),
  displayValues: PropTypes.node,
  renderItem: PropTypes.func,
  locked: PropTypes.bool,
  lockedTip: PropTypes.string,
  lockedTipPosition: PropTypes.oneOf(["top", "bottom"]),
  tip: PropTypes.string,
  letCreate: PropTypes.bool,
  letClear: PropTypes.bool,
  cache: PropTypes.bool,
  maxLength: PropTypes.number,
  closeOnChange: PropTypes.bool,
  query: PropTypes.func.isRequired, // return a promise
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  title: PropTypes.string,

  error: PropTypes.string,
  disabled: PropTypes.bool,
  whiteStyle: PropTypes.bool,
  style: PropTypes.object,
  selectStyles: PropTypes.object,
  inputStyle: PropTypes.object,
  rootClassName: PropTypes.string,
  noPadding: PropTypes.bool,

  handleChange: PropTypes.func,
  handleDropDownOpen: PropTypes.func,
  handleRemove: PropTypes.func,
  handleDropDownToggle: PropTypes.func,
  parseResponse: PropTypes.func,
  filterOptions: PropTypes.func,
  formatOption: PropTypes.func,
  saveRef: PropTypes.func,
  qaId: PropTypes.string,
  id: PropTypes.string,
  alwaysEmpty: PropTypes.bool,
  placeholder: PropTypes.string,
  showValues: PropTypes.bool,
  icon: PropTypes.node,
  suffixElement: PropTypes.shape({ className: PropTypes.string, text: PropTypes.node }),
  preOptions: PropTypes.node,
  hasValuesWithExpiryDate: PropTypes.bool,
  debounceWait: PropTypes.number,
  required: PropTypes.bool,
  disabledValues: PropTypes.arrayOf(PropTypes.string),
  onRestore: PropTypes.func,
  portalRef: PropTypes.object
};

AutoSelect.defaultProps = {
  multi: false,
  allowSameValueInOptions: false,
  values: null, // no [] here because it would lead to all components sharing one array
  locked: false,
  lockedTip: null,
  tip: null,
  letCreate: false,
  letClear: false,
  cache: true,
  maxLength: null,
  closeOnChange: false,

  error: null,
  disabled: false,

  parseResponse: (data) => (Array.isArray(data.entries) ? data.entries : data), // there's [].entries fn!
  formatOption: null,

  showValues: true,
  displayValues: false,
  hasValuesWithExpiryDate: false,
  debounceWait: 500,
  disabledValues: []
};

export default AutoSelect;
